'use client';
import { jsx } from 'react/jsx-runtime';
import { useQuery } from '@tanstack/react-query';
import { g as getBlokComponent, a as getAdditionalDataHook } from './defineBloks-shared-oexg9VmF.mjs';

const ClientComponent = ({ blok, preview, ...rest })=>{
    const Component = getBlokComponent(blok.component);
    if (!Component) {
        const errorMessage = `Component: "${blok.component}" not found`;
        return /*#__PURE__*/ jsx("div", {
            children: errorMessage
        });
    }
    if (typeof Component === "object") {
        if ("fetcher" in Component) {
            return /*#__PURE__*/ jsx(ClientComponentWithFetcher, {
                componentData: Component,
                preview: preview,
                ...rest,
                blok: blok
            });
        }
    }
    return /*#__PURE__*/ jsx(ClientComponentOnly, {
        preview: preview,
        ...rest,
        blok: blok,
        componentData: Component
    });
};
const ClientComponentOnly = ({ blok, componentData, preview, ...rest })=>{
    const { data, isLoading } = useAdditionalData({
        blok,
        preview
    });
    const Blok = typeof componentData === "function" ? componentData : componentData.blok;
    if (isLoading && typeof componentData === "object" && componentData.fallback) {
        const Fallback = componentData.fallback;
        return /*#__PURE__*/ jsx(Fallback, {
            preview: preview,
            ...rest,
            blok: blok
        });
    }
    return /*#__PURE__*/ jsx(Blok, {
        preview: preview,
        ...rest,
        ...data,
        blok: blok
    });
};
const ClientComponentWithFetcher = ({ blok, preview, componentData, ...rest })=>{
    const keyRes = componentData.getKey == null ? void 0 : componentData.getKey.call(componentData, {
        blok,
        preview
    });
    const key = Array.isArray(keyRes) ? keyRes : [
        keyRes
    ];
    const { data, isLoading } = useQuery({
        queryKey: [
            "blok",
            blok._uid,
            ...key.filter(Boolean)
        ],
        queryFn: ()=>componentData.fetcher({
                blok,
                preview
            })
    });
    const { data: additionalData, isLoading: additionalDataLoading } = useAdditionalData({
        blok,
        preview
    });
    const Blok = componentData.blok;
    const Fallback = componentData.fallback;
    if (isLoading || additionalDataLoading) {
        return Fallback ? /*#__PURE__*/ jsx(Fallback, {
            blok: blok,
            preview: preview
        }) : null; // TODO - Add global fallback option
    }
    return /*#__PURE__*/ jsx(Blok, {
        ...additionalData,
        ...rest,
        preview: preview,
        blok: blok,
        data: data
    });
};
const useAdditionalData = ({ blok, preview })=>{
    return useQuery({
        queryKey: [
            "blok",
            "additionalData",
            blok._uid
        ],
        queryFn: ()=>{
            const additionalDataHook = getAdditionalDataHook();
            return additionalDataHook({
                blok,
                preview
            });
        },
        refetchOnMount: false
    });
};

export { ClientComponent as C };
